import {
  createFrontendModule,
  ApiBlueprint,
} from '@backstage/frontend-plugin-api';
import { createApiFactory } from '@backstage/core-plugin-api';
import {
  techRadarApiRef,
  type TechRadarApi,
} from '@backstage-community/plugin-tech-radar';
import type { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';

// The tech-radar plugin (auto-discovered via `packages: all`) renders the
// /tech-radar page but ships no data of its own — it loads data through
// `techRadarApiRef`. We override that ref here with a static, in-repo dataset
// so the radar is self-contained (no tech-radar-backend, no external git URL).
// Edit `SEED` below to curate the Eurogate radar; it is the single source of truth.
//
// This mirrors the ApiBlueprint bridging pattern used by the Jira module: an
// app-scoped extension overriding a plugin-provided apiRef.

const SNAPSHOT_DATE = new Date('2026-07-01');

const SEED: TechRadarLoaderResponse = {
  quadrants: [
    { id: 'infrastructure', name: 'Infrastructure' },
    { id: 'languages', name: 'Languages & Frameworks' },
    { id: 'tools', name: 'Tools' },
    { id: 'platforms', name: 'Platforms' },
  ],
  rings: [
    { id: 'adopt', name: 'Adopt', color: '#5ba300' },
    { id: 'trial', name: 'Trial', color: '#009eb0' },
    { id: 'assess', name: 'Assess', color: '#c7ba00' },
    { id: 'hold', name: 'Hold', color: '#e09b96' },
  ],
  entries: [
    // Infrastructure
    entry('terraform', 'Terraform', 'infrastructure', 'adopt'),
    entry('ansible', 'Ansible', 'infrastructure', 'adopt'),
    entry('docker', 'Docker', 'infrastructure', 'adopt'),
    entry('atlantis', 'Atlantis', 'infrastructure', 'trial'),
    // Platforms
    entry('aws', 'AWS', 'platforms', 'adopt'),
    entry('postgresql', 'PostgreSQL', 'platforms', 'adopt'),
    entry('kafka', 'Kafka', 'platforms', 'assess'),
    entry('s3-data', 'S3 data flows', 'platforms', 'assess'),
    // Languages & Frameworks
    entry('nodejs', 'Node.js', 'languages', 'adopt'),
    entry('typescript', 'TypeScript', 'languages', 'adopt'),
    // Tools
    entry('bitbucket', 'Bitbucket', 'tools', 'adopt'),
    entry('backstage', 'Backstage', 'tools', 'trial'),
    entry('sonarcloud', 'SonarCloud', 'tools', 'trial'),
    entry('jira', 'Jira', 'tools', 'adopt'),
  ],
};

function entry(
  id: string,
  title: string,
  quadrant: string,
  ringId: string,
): TechRadarLoaderResponse['entries'][number] {
  return {
    key: id,
    id,
    title,
    quadrant,
    timeline: [{ date: SNAPSHOT_DATE, ringId }],
  };
}

class SeedTechRadarClient implements TechRadarApi {
  async load(): Promise<TechRadarLoaderResponse> {
    return SEED;
  }
}

const techRadarApi = ApiBlueprint.make({
  name: 'techRadar',
  params: defineParams =>
    defineParams(createApiFactory(techRadarApiRef, new SeedTechRadarClient())),
});

export const techRadarModule = createFrontendModule({
  pluginId: 'app',
  extensions: [techRadarApi],
});
